import {
  TfHullEvaluationAction,
  TfHullEvaluationFilters,
  TfHullEvaluationVessel,
} from "../models/tf-hull-evaluations.models";

interface Props {
  filters: TfHullEvaluationFilters;
  onChange: (filters: TfHullEvaluationFilters) => void;
  onClear: () => void;
  onOpenAction: (action: TfHullEvaluationAction) => void;
  vessels: TfHullEvaluationVessel[];
}

const unique = (values: string[]) =>
  Array.from(new Set(values.filter(Boolean))).sort((left, right) => left.localeCompare(right));

const filterLabelClassName = "block text-white";
const filterOptionLabelClassName = "flex items-center gap-1 text-white";
const filterControlClassName =
  "mt-1 h-8 w-full border border-white/60 bg-[#005b9a] px-2 text-white focus:border-white focus:outline-none focus:ring-1 focus:ring-white/70";
const filterNumberInputClassName =
  "h-8 min-w-0 border border-white/60 bg-[#005b9a] px-2 text-white placeholder:text-white/70 focus:border-white focus:outline-none focus:ring-1 focus:ring-white/70";

const TfHullEvaluationsFilters = ({ filters, onChange, onClear, onOpenAction, vessels }: Props) => {
  const update = <Key extends keyof TfHullEvaluationFilters>(key: Key, value: TfHullEvaluationFilters[Key]) => {
    onChange({ ...filters, [key]: value });
  };
  const toggle = (key: "contractTypes" | "statuses" | "hasEmail", value: string) => {
    const selected = filters[key];
    update(key, selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value]);
  };

  return (
    <aside
      className="rounded-lg bg-[#004f88] p-4 text-white shadow-sm lg:sticky lg:top-0"
      data-test="tf-hull-evaluations-filters"
    >
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-sm font-semibold">Filter pane</h2>
        <button
          type="button"
          onClick={onClear}
          className="rounded bg-[#f46c6c] px-2 py-1 text-[10px] font-semibold text-white"
        >
          Clear filters
        </button>
      </div>
      <div className="space-y-3 text-xs">
        <label className={filterLabelClassName}>
          IMO
          <select
            value={filters.imo}
            onChange={(event) => update("imo", event.target.value)}
            className={filterControlClassName}
            data-test="tf-hull-imo-filter"
          >
            <option value="">All</option>
            {unique(vessels.map((vessel) => vessel.imo)).map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </label>
        <label className={filterLabelClassName}>
          Vessel name
          <select
            value={filters.vesselName}
            onChange={(event) => update("vesselName", event.target.value)}
            className={filterControlClassName}
          >
            <option value="">All</option>
            {unique(vessels.map((vessel) => vessel.vesselName)).map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </label>
        <fieldset>
          <legend>Contract type</legend>
          <div className="mt-1 grid grid-cols-2 gap-1">
            {unique(vessels.map((vessel) => vessel.contractType)).map((value) => (
              <label key={value} className={filterOptionLabelClassName}>
                <input
                  type="checkbox"
                  checked={filters.contractTypes.includes(value)}
                  onChange={() => toggle("contractTypes", value)}
                />
                {value}
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset>
          <legend>Status</legend>
          <div className="mt-1 grid grid-cols-2 gap-1">
            {unique(vessels.map((vessel) => vessel.status)).map((value) => (
              <label key={value} className={filterOptionLabelClassName}>
                <input
                  type="checkbox"
                  checked={filters.statuses.includes(value)}
                  onChange={() => toggle("statuses", value)}
                />
                {value}
              </label>
            ))}
          </div>
        </fieldset>
        <label className={filterLabelClassName}>
          Vessel type
          <select
            value={filters.vesselType}
            onChange={(event) => update("vesselType", event.target.value)}
            className={filterControlClassName}
          >
            <option value="">All</option>
            {unique(vessels.map((vessel) => vessel.vesselType)).map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </label>
        <label className={filterLabelClassName}>
          Delivered since
          <div className="mt-1 grid grid-cols-2 gap-1">
            <input
              type="number"
              value={filters.deliveredFrom}
              onChange={(event) => update("deliveredFrom", event.target.value)}
              placeholder="Min"
              className={filterNumberInputClassName}
            />
            <input
              type="number"
              value={filters.deliveredTo}
              onChange={(event) => update("deliveredTo", event.target.value)}
              placeholder="Max"
              className={filterNumberInputClassName}
            />
          </div>
        </label>
        <label className={filterLabelClassName}>
          Entered since
          <div className="mt-1 grid grid-cols-2 gap-1">
            <input
              type="number"
              value={filters.enteredFrom}
              onChange={(event) => update("enteredFrom", event.target.value)}
              placeholder="Min"
              className={filterNumberInputClassName}
            />
            <input
              type="number"
              value={filters.enteredTo}
              onChange={(event) => update("enteredTo", event.target.value)}
              placeholder="Max"
              className={filterNumberInputClassName}
            />
          </div>
        </label>
        <label className={filterLabelClassName}>
          Manual reminder sent
          <select
            value={filters.manualReminder}
            onChange={(event) => update("manualReminder", event.target.value)}
            className={filterControlClassName}
          >
            <option value="">All</option>
            <option>No</option>
            <option>Yes</option>
          </select>
        </label>
        <label className={filterLabelClassName}>
          Hull assessment check
          <select
            value={filters.hullAssessment}
            onChange={(event) => update("hullAssessment", event.target.value)}
            className={filterControlClassName}
          >
            <option value="">All</option>
            <option>No</option>
            <option>Yes</option>
          </select>
        </label>
        <label className={filterLabelClassName}>
          Tech form status
          <select
            value={filters.techFormStatus}
            onChange={(event) => update("techFormStatus", event.target.value)}
            className={filterControlClassName}
          >
            <option value="">All</option>
            <option>NOT REQUESTED</option>
            <option>REQUESTED</option>
            <option>COMPLETED</option>
          </select>
        </label>
        <fieldset>
          <legend>Vessel has email in IMOS</legend>
          <div className="mt-1 grid grid-cols-2 gap-1">
            {[
              { label: "Yes", value: "true" },
              { label: "No", value: "false" },
            ].map((option) => (
              <label key={option.value} className={filterOptionLabelClassName}>
                <input
                  type="checkbox"
                  checked={filters.hasEmail.includes(option.value)}
                  onChange={() => toggle("hasEmail", option.value)}
                />
                {option.label}
              </label>
            ))}
          </div>
        </fieldset>
      </div>
      <div className="mt-5 border-t border-white/30 pt-4">
        <h3 className="text-xs font-semibold">Action pane</h3>
        <div className="mt-2 grid gap-2">
          <button
            type="button"
            onClick={() => onOpenAction("request")}
            className="w-full rounded bg-white/20 px-3 py-2 text-xs"
            data-test="tf-hull-open-request-action"
          >
            Tech Request
          </button>
          <button
            type="button"
            onClick={() => onOpenAction("reminder")}
            className="w-full rounded bg-white/20 px-3 py-2 text-xs"
            data-test="tf-hull-open-reminder-action"
          >
            Reminder
          </button>
        </div>
      </div>
    </aside>
  );
};

export default TfHullEvaluationsFilters;
